/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";

// import fallback image locally
import fallbackImageFile from "assets/images/course_fallback.png";

function CourseCard({
  image,
  title,
  description,
  label,
  action = false,
  exploreCard,
  moduleCount,
  purchased,
  externalLink
}) {
  const navigate = useNavigate();

  // keep one state for the image being used
  const [imgSrc, setImgSrc] = useState(image || fallbackImageFile);

  useEffect(() => {
    setImgSrc(image || fallbackImageFile);
  }, [image]);

  const handleImgError = () => {
    if (imgSrc !== fallbackImageFile) {
      setImgSrc(fallbackImageFile);
    }
  };

  return (
    <Card
      sx={{
        "&:hover .card-header": {
          transform: action && "translate3d(0, -20px, 0)",
        },
        display: "flex",
      }}
      style={{
        height: "100%",
        cursor: "pointer",
      }}
      onClick={exploreCard}
    >
      <MDBox
        position="relative"
        borderRadius="lg"
        className="card-header"
        sx={{ transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)" }}
      >
        <MDBox
          component="div"
          width="100%"
          position="relative"
          paddingTop="56.25%"
          overflow="hidden"
          borderRadius="lg"
          shadow="md"
        >
          <MDBox
            component="img"
            src={imgSrc}
            alt={title}
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            zIndex={1}
            onError={handleImgError}
          />
        </MDBox>
        <MDBox
          borderRadius="lg"
          shadow="md"
          width="100%"
          height="100%"
          position="absolute"
          left={0}
          top="0"
          sx={{
            backgroundImage: `url(${imgSrc})`,
            transform: "scale(0.94)",
            filter: "blur(12px)",
            backgroundSize: "cover",
          }}
        />
      </MDBox>


      <MDBox textAlign="center" pt={1} px={3} mt={1}
        style={{
          // add a border bottom to the card
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <MDTypography variant="h5" fontWeight="regular" sx={{
          mt: 1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          {/* On hover, show the entire title in a tooltip */}
          <Tooltip title={title}>
            {title}
          </Tooltip>
        </MDTypography>
        <MDTypography variant="body2" color="text" sx={{
          mt: 1.5,
          mb: 1,
          height: "3rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
          <Tooltip title={description}>
            {description}
          </Tooltip>
        </MDTypography>
      </MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={0.5}
        px={3}
        mb={0.5}
        lineHeight={1}
      >
        <MDBox
          mt={1}
          mb={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {purchased ? (
            <MDButton
              variant="outlined"
              color="success"
              fullWidth
            >
              Launch
            </MDButton>
          ) : (
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
            >
              Explore
            </MDButton>
          )}
          <MDBox ml={1}>
            <Tooltip title={purchased ? "Purchased" : "Not Purchased Yet"}>
              {purchased ? <LockOpenIcon /> : <LockIcon />}
            </Tooltip>
          </MDBox>
          <MDBox ml={1}>
            <Tooltip title={externalLink ? "Paid Course" : "Free Course"} >
              {externalLink ? <Icon color="warning">monetization_on</Icon> : <Icon color="success">money_off</Icon>}
            </Tooltip>
          </MDBox>
        </MDBox>
        <MDBox display="flex" alignItems="center">
          <Icon>subscriptions</Icon>
          <MDTypography variant="caption" ml={1}>
            {moduleCount} {moduleCount > 1 ? "Modules" : "Module"}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card >
  );
}

// Typechecking props for the BookingCard
CourseCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  action: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
};

export default CourseCard;
